import {
  AutogiroPage,
  BankruptcyPage,
  ContactInfoPage,
  DisbursementAccountPage,
  KycPage,
  LoadingPage,
} from '@resurs-internal/deposits-app-components';
import { useContext } from 'react';
import { useNavigate } from 'react-router';

import { CountryContext, KycSectionContext, SectionContext } from '@/contexts';
import {
  getSectionCount,
  getSectionType,
  hasSectionType,
  logout,
  SectionType,
  sendGTMApplicationProgress,
} from '@/helpers';
import { isAuthenticated, sendGTMFormInteraction } from '@/helpers';
import { type ApplicationFields, useCampaignCode } from '@/hooks';
import { lookupBankName } from '@/services';
import { fetchDocument } from '@/services';
import { type CountryTranslationResponseDTO } from '@/types/consumer-deposit-application-service';
import { type KycSurvey } from '@/types/know-your-customer-service';

export type SectionProps = {
  application: ApplicationFields;
  applicationId: string;
  countries?: CountryTranslationResponseDTO[];
  kycSurvey: KycSurvey;
  productName: string;
};

export const SectionResolver = (props: SectionProps) => {
  const { section, setNavigationDetails } = useContext(SectionContext);
  const {
    kycGroup,
    kycQuestion,
    navigateToNextGroup,
    navigateToNextQuestion,
    navigateToPreviousGroup,
    navigateToPreviousQuestion,
  } = useContext(KycSectionContext);
  const { country } = useContext(CountryContext);
  const navigate = useNavigate();
  const { appendCampaignCode } = useCampaignCode();
  const totalSections = getSectionCount(country, props.application.productType);

  const sectionType = getSectionType(
    section,
    country,
    props.application.productType,
  );

  const handleCancelApplication = async () => {
    await logout()
      .then(() => navigate(appendCampaignCode('/')))
      .catch(() => navigate('/error'));
  };

  const navigateToChooseDepositAccount = () => {
    navigate(appendCampaignCode('/choose-account-type'));
  };

  const navigateNextSection = (newSection: number = 1, part: number = 0) => {
    setNavigationDetails({
      part,
      section: section + newSection,
      subSection: 0,
    });
  };

  const navigatePrevSection = (newSection: number = 1, part: number = 0) => {
    setNavigationDetails({
      part,
      section: section - newSection,
      subSection: 0,
    });
  };

  const navigateSummary = () => {
    navigate('summary');
  };

  const navigateError = () => {
    navigate('/error');
  };
  const handleGTMProgress = (
    applicationId: string,
    productVariant: string,
    stepName: string,
    stepNumber: number,
  ) => {
    sendGTMApplicationProgress({
      applicationId,
      country,
      productVariant,
      stepName,
      stepNumber,
    });
  };
  const handleGTMFormInteraction = (
    formName: string,
    productVariant: string,
  ) => {
    sendGTMFormInteraction(formName, productVariant, country);
  };

  const renderContactInfo = () => (
    <ContactInfoPage
      country={country}
      navigateBack={navigateToChooseDepositAccount}
      navigateForward={navigateNextSection}
      section={section + 1}
      sendGTMApplicationProgress={handleGTMProgress}
      totalSections={totalSections}
      {...props}
      consents={props.application.consents}
      contactInfo={props.application.contactInfo}
      onCancel={handleCancelApplication}
    />
  );

  const renderDisbursementAccount = () => (
    <DisbursementAccountPage
      country={country}
      lookupBankName={lookupBankName}
      navigateBack={navigatePrevSection}
      navigateForward={navigateNextSection}
      section={section + 1}
      sendGTMApplicationProgress={handleGTMProgress}
      totalSections={totalSections}
      {...props}
      disbursementAccount={props.application.disbursementAccount}
      onCancel={handleCancelApplication}
    />
  );

  const renderAutogiro = () => (
    <AutogiroPage
      country={country}
      fetchDocument={fetchDocument}
      isAuthenticated={isAuthenticated()}
      lookupBankName={lookupBankName}
      navigateBack={navigatePrevSection}
      navigateForward={navigateNextSection}
      section={section + 1}
      sendGTMApplicationProgress={handleGTMProgress}
      sendGTMFormInteraction={handleGTMFormInteraction}
      totalSections={totalSections}
      {...props}
      autogiro={props.application.autogiroSettings}
      onCancel={handleCancelApplication}
    />
  );

  const renderBankruptcy = () => {
    const kYCquestions = props.kycSurvey.groups.flatMap((group) =>
      group.questions
        .filter((it) => it.displayCondition === undefined)
        .map((question) => question.id),
    );

    return (
      <BankruptcyPage
        country={country}
        navigateBack={navigatePrevSection}
        navigateForward={navigateNextSection}
        section={section + 1}
        sendGTMApplicationProgress={handleGTMProgress}
        {...props}
        bankruptcy={props.application.bankruptcy}
        onCancel={handleCancelApplication}
        totalPagesInASection={kYCquestions.length}
        totalSections={totalSections}
      />
    );
  };

  const renderKyc = () => {
    const bankruptcySectionIncluded = hasSectionType(
      country,
      props.application.productType,
      SectionType.Bankruptcy,
    );

    return (
      <KycPage
        country={country}
        hasBankruptcyQuestion={bankruptcySectionIncluded}
        kycAnswers={props.application.kycAnswers}
        kycCountries={props.countries || []}
        kycGroup={kycGroup}
        kycQuestion={kycQuestion}
        navigateBack={navigatePrevSection}
        navigateError={navigateError}
        navigateForward={navigateSummary}
        navigateToNextGroup={navigateToNextGroup}
        navigateToNextQuestion={navigateToNextQuestion}
        navigateToPreviousGroup={navigateToPreviousGroup}
        navigateToPreviousQuestion={navigateToPreviousQuestion}
        onCancel={handleCancelApplication}
        section={section}
        sendGTMApplicationProgress={handleGTMProgress}
        totalSections={totalSections}
        {...props}
      />
    );
  };

  switch (sectionType) {
    case SectionType.ContactInfo:
      return renderContactInfo();
    case SectionType.DisbursementAccount:
      return renderDisbursementAccount();
    case SectionType.Autogiro:
      return renderAutogiro();
    case SectionType.Bankruptcy:
      return renderBankruptcy();
    case SectionType.Kyc:
      return renderKyc();
    default:
      return <LoadingPage country={country} />;
  }
};
